import React from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Space, Typography, Row, Col, Image } from "antd";
import { PhoneOutlined } from "@ant-design/icons";

import { BLUE_DARK } from "../../constant/colors";
import { sizes } from "../../constant/styles";
import { withUTMParams } from "../../utils/urlFormatter";
import { trackPendoEvent } from "../../utils/pendo/pendoUtils";
import { useStandardizedTracking } from "../../utils/pendo/useStandardizedTracking";
import { trackApLinkClick } from "../../utils/pendo/trackApLinkClick";
import { PendoEvents } from "../../constant/pendoEvents";
import { prepareDataForPendo } from "../../utils/pendo";
import { selectUser, selectEnvironment } from "../../store/selector";

const { Text } = Typography;

// Single source for each anchor's utm_content — used for BOTH the URL tag and the
// LINK_CLICK event so they can't diverge.
const REPORT_CONTACT_UTM = {
  url: "report_contact_provider_application",
  website: "report_contact_program_website",
  pharmacyWebsite: "report_contact_pharmacy_website"
};

const PatientReportContact = ({ assistanceProgram }) => {
  const { t } = useTranslation();
  const { patientId } = useParams();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const account = useSelector(selectEnvironment);
  const { track, FeatureGroups } = useStandardizedTracking();

  const handleProgramWebsiteClick = () => {
    if (patientId && user && account && assistanceProgram) {
      const eventData = prepareDataForPendo({
        applicationId: assistanceProgram.currentApplication,
        patientId: patientId,
        potentialSaving: assistanceProgram,
        user,
        account
      });
      trackPendoEvent(dispatch, PendoEvents.PROGRAM_WEBSITE_CLICKED, eventData);
    }
  };

  const trackLinkClick = (contentKey, url) =>
    // the `assistanceProgram` prop here is potential-saving-shaped (see handleProgramWebsiteClick),
    // so pass it as potentialSaving too → restores apa_id (application) + pap_id join keys.
    trackApLinkClick(track, {
      contentKey,
      url,
      featureGroup: FeatureGroups.POTENTIAL_SAVINGS,
      assistanceProgram,
      potentialSaving: assistanceProgram
    });

  return (
    <Row>
      <Col span={24} style={{ paddingRight: "16px", maxHeight: "70px" }}>
        <Space size={16}>
          {assistanceProgram && assistanceProgram.url && (
            <Space direction="horizontal" style={{ alignItems: "center" }}>
              <Image
                src={`${process.env.PUBLIC_URL}/images/assistance-program-website.png`}
                preview={false}
                width={15}
                height={12}
              />
              <a
                style={{ color: BLUE_DARK, fontSize: sizes.medium }}
                href={withUTMParams(assistanceProgram.url, { content: REPORT_CONTACT_UTM.url })}
                target="_blank"
                rel="noreferrer"
                onClick={() => trackLinkClick(REPORT_CONTACT_UTM.url, assistanceProgram.url)}
              >
                {t("patientPotentialSavings.application_link")}
              </a>
            </Space>
          )}
          {assistanceProgram && assistanceProgram.website && (
            <Space direction="horizontal" align="start">
              <Image
                src={`${process.env.PUBLIC_URL}/images/assistance-program-website.png`}
                preview={false}
                width={15}
                height={12}
              />
              <a
                style={{ color: BLUE_DARK, fontSize: sizes.medium }}
                href={withUTMParams(assistanceProgram.website, { content: REPORT_CONTACT_UTM.website })}
                target="_blank"
                rel="noreferrer"
                onClick={() => {
                  handleProgramWebsiteClick();
                  trackLinkClick(REPORT_CONTACT_UTM.website, assistanceProgram.website);
                }}
              >
                {t("patientPotentialSavings.program_website")}
              </a>
            </Space>
          )}
          {assistanceProgram && assistanceProgram.pharmacyWebsite && (
            <Space direction="horizontal" align="start">
              <Image
                src={`${process.env.PUBLIC_URL}/images/assistance-program-website.png`}
                preview={false}
                width={15}
                height={12}
              />
              <a
                style={{ color: BLUE_DARK, fontSize: sizes.medium }}
                href={withUTMParams(assistanceProgram.pharmacyWebsite, { content: REPORT_CONTACT_UTM.pharmacyWebsite })}
                target="_blank"
                rel="noreferrer"
                onClick={() => trackLinkClick(REPORT_CONTACT_UTM.pharmacyWebsite, assistanceProgram.pharmacyWebsite)}
              >
                {t("patientPotentialSavings.pharmacy_website")}
              </a>
            </Space>
          )}
          {assistanceProgram && assistanceProgram.phone1 && assistanceProgram.phone1.length && (
            <Space direction="horizontal" style={{ display: "flex", alignItems: "center" }}>
              <PhoneOutlined style={{ color: BLUE_DARK }} />
              <Text style={{ color: BLUE_DARK, fontSize: sizes.medium }}>{assistanceProgram.phone1}</Text>
            </Space>
          )}
          {assistanceProgram && assistanceProgram.phone2 && assistanceProgram.phone2.length && (
            <Space direction="horizontal" style={{ display: "flex", alignItems: "center" }}>
              <PhoneOutlined style={{ color: BLUE_DARK }} />
              <Text style={{ color: BLUE_DARK, fontSize: sizes.medium }}>{assistanceProgram.phone2}</Text>
            </Space>
          )}
          {assistanceProgram && assistanceProgram.address && assistanceProgram.address.length && (
            <Space direction="horizontal" align="start">
              <Image
                src={`${process.env.PUBLIC_URL}/images/assistance-program-address.png`}
                preview={false}
                width={10}
                height={13}
              />
              <Text style={{ color: BLUE_DARK, fontSize: sizes.medium }}>{assistanceProgram.address}</Text>
            </Space>
          )}
        </Space>
      </Col>
    </Row>
  );
};

export default PatientReportContact;
